import 'react-pdf/dist/Page/TextLayer.css';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FileX2 } from 'lucide-react';
import { memo, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { PAGE_ATTR } from '../hooks/usePageScroll';
import { escapeHtml, inPassage, markFigure } from '../model/highlight';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

const BASE_WIDTH = 612; // a PDF page is 612pt wide at 100%
const DEFAULT_RATIO = 1.3;
const NEARBY = '150% 0px'; // pages within one and a half views of the visible area are drawn

export type PdfLayout = { pages: number; ratio: number };

type Props = {
  url: string;
  zoom: number;
  layout: PdfLayout;
  scrollRef: RefObject<HTMLDivElement | null>;
  passage: string | null;
  figure?: number | null;
  /** The one page the passage or figure is tinted on: a short phrase would also match on other pages. */
  tintPage: number;
  onLoaded: (layout: PdfLayout) => void;
};

type PageProps = { pageNumber: number; width: number; ratio: number; scrollRef: RefObject<HTMLDivElement | null>; passage: string | null; figure: number | null };

/** One page, drawn only while near the visible area, so a 168-page report does not hold 168 canvases. It keeps its
 * height either way, so the scrollbar and the jumps to a page stay right. */
const LazyPage = memo(function LazyPage({ pageNumber, width, ratio, scrollRef, passage, figure }: PageProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [isNear, setNear] = useState(false);
  const height = Math.round(width * ratio);
  const renderText = useMemo(
    () => ({ str }: { str: string }) => (inPassage(str, passage) ? `<mark>${escapeHtml(str)}</mark>` : markFigure(str, figure)),
    [passage, figure],
  );

  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const observer = new IntersectionObserver(([entry]) => { setNear(entry?.isIntersecting ?? false); }, { root: scrollRef.current, rootMargin: NEARBY });
    observer.observe(box);
    return () => { observer.disconnect(); };
  }, [scrollRef]);

  const placeholder = <Skeleton variant="rectangular" width={width} height={height} sx={{ bgcolor: 'background.paper' }} />;
  return (
    <Box ref={boxRef} {...{ [PAGE_ATTR]: pageNumber }} sx={{ ...pageSx, minHeight: height, width }}>
      {isNear ? (
        <Page key={width} suspense={false} pageNumber={pageNumber} width={width} renderAnnotationLayer={false} loading={placeholder} customTextRenderer={renderText} />
      ) : placeholder}
    </Box>
  );
});

/** The whole report as one column of pages, with selectable text; the passage or figure being pointed at is tinted. */
export function PdfCanvas({ url, zoom, layout, scrollRef, passage, figure = null, tintPage, onLoaded }: Props) {
  const width = Math.round(BASE_WIDTH * zoom);
  const loading = <Skeleton variant="rectangular" width={width} height={Math.round(width * DEFAULT_RATIO)} sx={{ bgcolor: 'background.paper' }} />;

  return (
    // suspense={false}: a PDF that fails to load shows the message below instead of crashing the whole page.
    <Document
      suspense={false}
      file={url}
      onLoadSuccess={(pdf) => {
        // Pages are laid out from the first page's shape, before any is drawn.
        void pdf.getPage(1).then(
          (first) => {
            const { width: w, height: h } = first.getViewport({ scale: 1 });
            onLoaded({ pages: pdf.numPages, ratio: h / w });
          },
          () => { onLoaded({ pages: pdf.numPages, ratio: DEFAULT_RATIO }); },
        );
      }}
      loading={loading}
      error={<Missing />}
    >
      <Stack sx={{ gap: 2 }}>
        {Array.from({ length: layout.pages }, (_, i) => i + 1).map((n) => (
          <LazyPage
            key={n}
            pageNumber={n}
            width={width}
            ratio={layout.ratio}
            scrollRef={scrollRef}
            passage={n === tintPage ? passage : null}
            figure={n === tintPage ? figure : null}
          />
        ))}
      </Stack>
    </Document>
  );
}

function Missing() {
  return (
    <Stack sx={{ alignItems: 'center', textAlign: 'center', gap: 1, px: 4, py: 8, maxWidth: 420 }}>
      <Box sx={{ display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: 1.25, bgcolor: 'surface.sunken', color: 'text.secondary' }}>
        <FileX2 size={16} aria-hidden />
      </Box>
      <Typography variant="h4" component="p">The PDF is not available</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        This report's file could not be loaded. Its summary and Ask still work from the stored text.
      </Typography>
    </Stack>
  );
}

const pageSx = {
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(26, 26, 25, 0.08), 0 8px 24px rgba(26, 26, 25, 0.06)',
  // The tint sits behind the report's own text, which stays as printed.
  '& mark': { color: 'transparent', bgcolor: 'color-mix(in srgb, var(--mui-palette-primary-main) 22%, transparent)', borderRadius: '2px' },
} as const;
